import { useState } from 'react';
import { Alert, Box, IconButton, Link, Tooltip, Typography } from '@mui/material';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import { Link as RouterLink } from 'react-router-dom';
import { HINT_SEVERITY_MAP } from '../utils/hintEngine';

const STORAGE_KEY = 'agendario_hint_expanded';

function readExpanded() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === 'true';
  } catch {
    return true;
  }
}

function writeExpanded(value) {
  try { localStorage.setItem(STORAGE_KEY, String(value)); } catch {}
}

/**
 * Componente de Dica Contextual
 * Float fixo posicionado à esquerda do FAB de suporte (canto inferior direito).
 * X → minimiza para ícone de lâmpada. Estado persiste via localStorage.
 * Mobile (< md): não renderiza — floats atrapalhavam o uso da tela.
 *
 * @param {object} hint - Objeto com { type, icon, message, action: { label, link } }
 * @param {function} onActionClick - Callback quando não há link (ex: abrir modal)
 */
export default function ContextualHint({ hint, onActionClick = null }) {
  const [expanded, setExpanded] = useState(readExpanded);

  const collapse = () => { setExpanded(false); writeExpanded(false); };
  const expand   = () => { setExpanded(true);  writeExpanded(true);  };

  if (!hint) return null;

  const severity = HINT_SEVERITY_MAP[hint.type] || 'info';
  const accentColor = { info: 'info.main', success: 'success.main', warning: 'warning.main', error: 'error.main' }[severity] || 'info.main';
  const actionLabel = hint.action?.label;
  const actionLink  = hint.action?.link;

  // Estado minimizado: ícone de lâmpada à esquerda do FAB de suporte
  if (!expanded) {
    return (
      <Box sx={{ position: 'fixed', bottom: 28, right: 96, zIndex: 1400, display: { xs: 'none', md: 'block' } }}>
        <Tooltip title="Ver dica" placement="top">
          <IconButton
            onClick={expand}
            size="small"
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 2,
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <LightbulbIcon sx={{ color: 'warning.main', fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  // Estado expandido: card à esquerda do FAB de suporte
  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 90,
        zIndex: 1400,
        maxWidth: 380,
        width: 'calc(100vw - 130px)',
        display: { xs: 'none', md: 'block' },
      }}
    >
      <Alert
        severity={severity}
        variant="outlined"
        icon={<LightbulbIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
        onClose={collapse}
        sx={{
          borderRadius: 2,
          bgcolor: 'background.paper',
          opacity: 0.93,
          boxShadow: 3,
          borderLeftWidth: 3,
          borderLeftStyle: 'solid',
          borderLeftColor: accentColor,
          py: 0.75,
          '& .MuiAlert-message': { width: '100%' },
          '& .MuiAlert-icon': { alignItems: 'center' },
          '& .MuiAlert-action': { alignItems: 'center', pt: 0 },
        }}
      >
        <Typography variant="body2" component="div" sx={{ lineHeight: 1.5, fontSize: '0.85rem' }}>
          {hint.icon && (
            <Typography component="span" sx={{ mr: 0.5, fontSize: '1.1em', lineHeight: 1, verticalAlign: 'baseline' }}>
              {hint.icon}
            </Typography>
          )}
          <Typography component="span" sx={{ fontSize: '1.0em' }}>
            {hint.message}
          </Typography>
          {actionLabel && (actionLink || onActionClick) && (
            <>
              {' '}
              {actionLink ? (
                <Link component={RouterLink} to={actionLink} underline="always">{actionLabel}</Link>
              ) : (
                <Link component="button" type="button" onClick={onActionClick} underline="always">{actionLabel}</Link>
              )}
            </>
          )}
        </Typography>
      </Alert>
    </Box>
  );
}
